import { useEffect, useState } from 'react';
import { Bell, X } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DISMISS_KEY = 'emina_push_dismissed';

function b64ToU8(b64) {
  const padding = '='.repeat((4 - (b64.length % 4)) % 4);
  const base64 = (b64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

export default function PushSubscribePrompt() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
    try {
      if (localStorage.getItem(DISMISS_KEY)) return;
      if (Notification.permission === 'granted' || Notification.permission === 'denied') return;
    } catch { return; }
    // delay popup — fetched from admin settings (falls back to 60s)
    let timer;
    fetch(`${API}/public/popup-delays`).then(r => r.ok ? r.json() : { notification_delay_seconds: 60 })
      .then(cfg => {
        const ms = (cfg.notification_delay_seconds ?? 60) * 1000;
        timer = setTimeout(() => setShow(true), ms);
      }).catch(() => {
        timer = setTimeout(() => setShow(true), 60000);
      });
    return () => clearTimeout(timer);
  }, []);

  async function subscribe() {
    try {
      const reg = await navigator.serviceWorker.register('/sw.js');
      await navigator.serviceWorker.ready;
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') { toast.message('Dozvola odbijena'); setShow(false); return; }
      const r = await fetch(`${API}/public/push/public-key`);
      const { public_key } = await r.json();
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: b64ToU8(public_key),
      });
      const raw = sub.toJSON();
      await fetch(`${API}/public/push/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          endpoint: raw.endpoint,
          keys: raw.keys,
          user_agent: navigator.userAgent,
        }),
      });
      toast.success('Pretplaćeni ste na obavijesti');
      setShow(false);
    } catch (e) {
      console.error(e);
      toast.error('Pretplata nije uspjela');
    }
  }

  function dismiss() {
    try { localStorage.setItem(DISMISS_KEY, '1'); } catch {}
    setShow(false);
  }

  if (!show) return null;
  return (
    <div className="fixed bottom-4 right-4 z-40 bg-white dark:bg-[#111827] border border-gray-200 dark:border-gray-800 rounded-xl shadow-lg p-4 max-w-sm animate-in slide-in-from-bottom-4" data-testid="push-subscribe-prompt">
      <div className="flex items-start gap-3">
        <div className="p-2 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-600 shrink-0"><Bell size={18} /></div>
        <div className="flex-1">
          <div className="font-semibold text-sm text-gray-900 dark:text-gray-100 mb-1">Primajte obavijesti</div>
          <div className="text-xs text-gray-500 dark:text-gray-400 mb-3">Aktivirajte push obavijesti i budite u tijeku s najnovijim vijestima.</div>
          <div className="flex items-center gap-2">
            <button onClick={subscribe} className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white text-xs font-medium rounded-lg" data-testid="push-subscribe-btn">
              Uključi obavijesti
            </button>
            <button onClick={dismiss} className="px-3 py-1.5 text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300" data-testid="push-dismiss-btn">
              Ne, hvala
            </button>
          </div>
        </div>
        <button onClick={dismiss} className="text-gray-400 hover:text-gray-600" aria-label="Zatvori"><X size={16} /></button>
      </div>
    </div>
  );
}
